// Iter 30q: enhanced autocomplete with "🆕 Crea scheda" inline workflow.
// - Mentre digiti, dopo 500ms debounce chiede al server check-name (fuzzy 0.85).
// - Se nessun match esatto e nessun match autocomplete locale, mostra il
//   bottone inline "Crea scheda: COGNOME NOME" oppure "Forse intendevi X?".
// - Click → mini-dialog con cognome/nome/ruolo per creare al volo.
import { useState, useEffect, useRef, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Check, UserPlus, Sparkles, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const RUOLI = [
  "Operaio",
  "Capo Cantiere",
  "Tecnico",
  "Saldatore",
  "Carpentiere",
  "Elettricista",
  "Magazziniere",
  "Apprendista",
  "Impiegato",
  "Altro",
];

// Estrae cognome + nome da un'unica stringa "ROSSI MARIO" o "Mario Rossi"
function splitFullName(raw) {
  const s = (raw || "").trim();
  if (!s) return { cognome: "", nome: "" };
  const parts = s.split(/\s+/);
  if (parts.length === 1) return { cognome: parts[0], nome: "" };
  // Convenzione italiana del file: "COGNOME NOME"
  const cognome = parts[0];
  const nome = parts.slice(1).join(" ");
  return { cognome, nome };
}

export default function EmployeeAutocomplete({
  items = [],
  value,
  onChange,
  onSelect,
  placeholder = "Cognome o Nome (3 lettere)",
  triggerLength = 3,
  testId = "employee-autocomplete",
  compact = false,
  // Iter 30q: callback opzionale invocata quando un nuovo dipendente viene
  // creato via il dialog inline. Riceve il documento creato dal backend.
  onCreated,
  // Iter 33: se false, l'utente NON può creare dipendenti dai rapportini
  // (niente hint "Crea"). Se requiresConfirmation, il nuovo dipendente
  // resterà "da confermare" dall'amministratore.
  canCreate = true,
  requiresConfirmation = false,
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);

  // ----- Iter 30q: check-name debounced -----
  const [checkResult, setCheckResult] = useState(null); // {exact, fuzzy}
  const [checking, setChecking] = useState(false);
  const lastCheckedRef = useRef("");
  // Stato del mini-dialog di creazione
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    cognome: "", nome: "", ruolo: "Operaio", alias: "",
  });
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    if (!value || value.length < triggerLength) return [];
    return items
      .filter((it) => it.label.toLowerCase().includes(value.toLowerCase()))
      .slice(0, 8);
  }, [items, value, triggerLength]);

  // Lookup remoto debounced per fuzzy match
  useEffect(() => {
    const v = (value || "").trim();
    if (v.length < triggerLength) {
      setCheckResult(null);
      return;
    }
    // Se il valore matcha già un item locale → niente chiamata
    if (filtered.some((it) => it.label.toLowerCase() === v.toLowerCase())) {
      setCheckResult({ exact: { local: true }, fuzzy: [] });
      return;
    }
    const t = setTimeout(async () => {
      if (lastCheckedRef.current === v) return;
      lastCheckedRef.current = v;
      setChecking(true);
      try {
        const { data } = await api.post("/employees/check-name", {
          name: v,
          threshold: 0.85,
        });
        setCheckResult(data);
      } catch (_) {
        setCheckResult(null);
      } finally {
        setChecking(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [value, triggerLength, filtered]);

  useEffect(() => {
    function onDocClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleKeyDown = (e) => {
    // Iter 31q: TAB completa il primo suggerimento e lascia che il browser
    // passi naturalmente al prossimo input.
    if (e.key === "Tab" && !e.shiftKey) {
      const candidate = filtered[open ? highlight : 0];
      if (candidate) {
        onSelect?.(candidate);
        setOpen(false);
        // Non preventDefault → focus passa alla prossima cella naturalmente
        return;
      }
    }
    if (!open || filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = filtered[highlight];
      if (it) {
        onSelect?.(it);
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Decisione: mostrare hint "crea nuovo"?
  const trimmed = (value || "").trim();
  const showCreateHint =
    canCreate &&
    trimmed.length >= triggerLength &&
    filtered.length === 0 &&
    checkResult &&
    !checkResult.exact &&
    !checking;
  const fuzzyHit =
    checkResult && checkResult.fuzzy && checkResult.fuzzy.length > 0
      ? checkResult.fuzzy[0]
      : null;

  const applyFuzzy = () => {
    if (!fuzzyHit) return;
    const emp = fuzzyHit.employee;
    onSelect?.({
      id: emp.id,
      label: `${emp.cognome} ${emp.nome}`,
      raw: emp,
    });
  };

  const startCreate = () => {
    const { cognome, nome } = splitFullName(trimmed);
    setCreateForm({
      cognome: cognome.toUpperCase(),
      nome: nome.charAt(0).toUpperCase() + nome.slice(1).toLowerCase(),
      ruolo: "Operaio",
      alias: trimmed, // l'esatta stringa digitata diventa alias
    });
    setCreateOpen(true);
  };

  const confirmCreate = async () => {
    const cognome = (createForm.cognome || "").trim();
    const nome = (createForm.nome || "").trim();
    if (!cognome) {
      toast.error("Il cognome è obbligatorio");
      return;
    }
    setCreating(true);
    try {
      const aliases = [];
      const al = (createForm.alias || "").trim();
      if (al && al.toLowerCase() !== `${cognome} ${nome}`.toLowerCase()) {
        aliases.push(al);
      }
      const { data: created } = await api.post("/employees", {
        cognome,
        nome,
        ruolo: createForm.ruolo,
        attivo: true,
        mostra_nei_rapportini: true,
        aliases,
      });
      if (created?.pending_confirmation) {
        toast.success(
          `Dipendente ${cognome} ${nome} inserito: in attesa di conferma dell'amministratore`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Dipendente creato: ${cognome} ${nome}`);
      }
      setCreateOpen(false);
      setCheckResult({ exact: created, fuzzy: [] });
      // Notifica il parent: usa subito il nuovo dipendente nella riga corrente
      onSelect?.({
        id: created.id,
        label: `${created.cognome} ${created.nome}`,
        raw: created,
      });
      onCreated?.(created);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Creazione fallita");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        data-testid={testId}
        value={value || ""}
        onChange={(e) => {
          onChange?.(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onDoubleClick={(e) => {
          // Iter 30n: doppio-click SVUOTA il campo
          onChange?.("");
          setOpen(true);
          setHighlight(0);
          try { e.target.focus(); } catch (_) { /* noop */ }
        }}
        onKeyDown={handleKeyDown}
        placeholder={compact ? "Cognome…" : placeholder}
        className={compact ? "h-9 text-sm" : "h-12 text-base"}
        autoComplete="off"
        title="Doppio-click per modificare"
      />

      {/* Autocomplete dropdown */}
      {open && filtered.length > 0 && (
        <ul
          data-testid={`${testId}-list`}
          className="absolute z-40 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto"
        >
          {filtered.map((it, idx) => (
            <li
              key={it.id}
              data-testid={`${testId}-item-${idx}`}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect?.(it);
                setOpen(false);
              }}
              className={`px-3 py-3 text-sm cursor-pointer flex items-center justify-between hover:bg-slate-100 ${
                idx === highlight ? "bg-slate-100" : ""
              }`}
            >
              <span>{it.label}</span>
              {it.label === value && <Check className="h-4 w-4 text-[#0044FF]" />}
            </li>
          ))}
        </ul>
      )}

      {/* "Digita almeno N lettere" */}
      {open && value && value.length > 0 && value.length < triggerLength && (
        <div className="absolute z-40 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-sm px-3 py-2 text-xs text-slate-500">
          Digita almeno {triggerLength} lettere…
        </div>
      )}

      {/* Iter 30q: Hint "crea nuovo dipendente" + suggerimento fuzzy */}
      {showCreateHint && (
        <div
          className="mt-1 flex flex-wrap items-center gap-1.5 text-xs"
          data-testid={`${testId}-create-hint`}
        >
          {fuzzyHit ? (
            <>
              <span className="text-amber-700">Forse intendevi</span>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); applyFuzzy(); }}
                data-testid={`${testId}-fuzzy-use`}
                className="px-2 py-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-900 font-bold inline-flex items-center gap-1"
              >
                <Sparkles className="h-3 w-3" />
                {fuzzyHit.employee.cognome} {fuzzyHit.employee.nome}
              </button>
              <span className="text-slate-400">oppure</span>
            </>
          ) : (
            <span className="text-slate-500">Nuovo dipendente?</span>
          )}
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); startCreate(); }}
            data-testid={`${testId}-create-button`}
            className="px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold inline-flex items-center gap-1"
          >
            <UserPlus className="h-3 w-3" />
            Crea: {trimmed.toUpperCase()}
          </button>
        </div>
      )}
      {checking && trimmed.length >= triggerLength && filtered.length === 0 && (
        <div className="mt-1 text-[11px] text-slate-400 flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Verifica nome…
        </div>
      )}

      {/* Mini-dialog creazione */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid={`${testId}-create-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-emerald-600" />
              Nuovo dipendente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold">Cognome *</Label>
                <Input
                  value={createForm.cognome}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, cognome: e.target.value })
                  }
                  className="h-11 mt-1"
                  autoFocus
                  data-testid={`${testId}-create-cognome`}
                />
              </div>
              <div>
                <Label className="text-xs font-bold">Nome</Label>
                <Input
                  value={createForm.nome}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, nome: e.target.value })
                  }
                  className="h-11 mt-1"
                  data-testid={`${testId}-create-nome`}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold">Ruolo</Label>
              <select
                value={createForm.ruolo}
                onChange={(e) =>
                  setCreateForm({ ...createForm, ruolo: e.target.value })
                }
                className="h-11 mt-1 w-full rounded-md border border-slate-200 px-3 text-sm"
                data-testid={`${testId}-create-ruolo`}
              >
                {RUOLI.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold">
                Alias (variante di scrittura)
              </Label>
              <Input
                value={createForm.alias}
                onChange={(e) =>
                  setCreateForm({ ...createForm, alias: e.target.value })
                }
                placeholder="es. Mario Rossi"
                className="h-11 mt-1"
                data-testid={`${testId}-create-alias`}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Salvato automaticamente per riconoscere il dipendente la
                prossima volta che digiterai questa variante.
              </p>
            </div>
            {requiresConfirmation && (
              <div className="rounded-md border border-sky-200 bg-sky-50 p-2 text-xs text-sky-900" data-testid={`${testId}-pending-note`}>
                Il dipendente sarà utilizzabile subito nel rapportino, ma resterà
                <span className="font-bold"> "da confermare"</span>: l'amministratore
                riceverà un avviso al suo prossimo accesso.
              </div>
            )}
            {fuzzyHit && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                <span className="font-bold">Attenzione:</span> esiste già{" "}
                <span className="font-bold">
                  {fuzzyHit.employee.cognome} {fuzzyHit.employee.nome}
                </span>{" "}
                ({Math.round(fuzzyHit.similarity * 100)}% simile). Verifica di
                non duplicare la stessa persona.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={confirmCreate}
              disabled={creating || !createForm.cognome.trim()}
              data-testid={`${testId}-create-confirm`}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4 mr-1" />
              )}
              Crea dipendente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
